import React, { useState, useRef, useEffect } from 'react';
import { Box, Button, Stack, Typography, Paper, CircularProgress, Tooltip } from '@mui/material';
import Carousel from 'react-material-ui-carousel';
import { useNavigate } from 'react-router-dom';
import MarketPlaceBanner from '../../../asset/images/agentMarketplaceHomeBanner.jpg';
import { LogButtonEventToGA } from '../../../shared/googleAnalytics/GoogleAnalytics';
import { UseLeaderboardStatistics } from '../../../shared/customHooks/useLeaderboardStatistics/UseLeaderboardStatistics';
import { useGetAgentsListQuery } from '../../../data/api/endPointsSlice';
import { ApiError, showSnackbarErrorMessage } from '../../../shared/utiles/Utiles';
import {
  findAgentButtonClickedText,
  publishAgentButtonClickedText,
} from '../../../shared/googleAnalytics/GoogleAnalyticsConstants';

interface CarouselItem {
  name: string;
  description: string;
  backGround: string;
  buttons?: boolean;
}

const AgentInnovators: React.FC = () => {
  const navigate = useNavigate();
  const [carouselActiveIndex, setCarouselActiveIndex] = useState(0);
  const carouselIndicatorRef = useRef<HTMLDivElement>(null);

  // Fetch agents data
  const {
    data: agentList = [],
    isLoading: agentListLoading,
    error: agentListError,
    refetch: refetchAgentList,
  } = useGetAgentsListQuery();

  // Use the leaderboard statistics hook to get top publishers
  const { topPublishers } = UseLeaderboardStatistics(agentList);

  const carouselItems: CarouselItem[] = [
    {
      name: 'Top Three Publishers',
      description: 'Meet the leading contributors shaping the future of AI automation',
      backGround: MarketPlaceBanner,
    },
    {
      name: 'Explore and Publish AI Agents for industry solutions',
      description:
        'Explore, Try out and Share your AI agents in the marketplace\nwhere the AI workforce builds and orchestrates AI agents for enterprise flows.',
      backGround: MarketPlaceBanner,
      buttons: true,
    },
  ];

  useEffect(() => {
    if (agentListError) {
      showSnackbarErrorMessage(agentListError as ApiError);
      return;
    }
    refetchAgentList();
  }, [agentListError, refetchAgentList]);

  const handleFindAgent = () => {
    LogButtonEventToGA(findAgentButtonClickedText);
    navigate('/agent_marketplace'); // Redirect to the /find-agent route
  };

  const handleRedirect = () => {
    LogButtonEventToGA(publishAgentButtonClickedText);
    navigate('/publisher');
  };

  const CarouselItemComponent = ({
    item,
    handleFindAgent,
    handleRedirect,
  }: {
    item: CarouselItem;
    handleFindAgent?: () => void;
    handleRedirect?: () => void;
  }) => {
    return (
      <Paper
        sx={{
          height: '415px',
          backgroundImage: `url(${item.backGround})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          paddingTop: '45px',
        }}
      >
        <Box sx={{ width: '100%', textAlign: 'center', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2rem', md: '2.5rem' },
                mb: 2,
                lineHeight: 1.2,
              }}
            >
              {item.name}
            </Typography>

            {item.description.includes('\n') ? (
              <>
                <Typography>{item.description.split('\n')[0]}</Typography>
                <Typography
                  sx={{
                    mb: 4,
                  }}
                >
                  {item.description.split('\n')[1]}
                </Typography>
              </>
            ) : (
              <Typography
                sx={{
                  mb: 4,
                }}
              >
                {item.description}
              </Typography>
            )}

            {item.buttons && (
              <Stack direction="row" spacing={3} justifyContent="center">
                <Button
                  variant="contained"
                  onClick={handleFindAgent}
                  sx={{
                    textTransform: 'none',
                    px: 2,
                    py: 1.5,
                    backgroundColor: '#000',
                    fontSize: '.8rem',
                  }}
                >
                  Find an Agent
                </Button>
                <Button
                  variant="contained"
                  onClick={handleRedirect}
                  sx={{
                    textTransform: 'none',
                    px: 2,
                    py: 1.5,
                    backgroundColor: '#e9ecef',
                    color: '#000',
                    cursor: 'pointer',
                    fontSize: '.8rem',
                  }}
                >
                  Publish your Agent
                </Button>
              </Stack>
            )}

            {/* Second slide content - showing publishers */}
            {!item.buttons && item.name === 'Top Three Publishers' && (
              <>
                {agentListLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 4, mt: 4 }}>
                    {topPublishers.length > 0 ? (
                      topPublishers.slice(0, 3).map((publisher, index) => (
                        <Paper
                          elevation={3}
                          key={index}
                          sx={{ p: 3, width: '320px', textAlign: 'center', backgroundColor: '#f8f9fa' }}
                        >
                          <Tooltip title={publisher.name} arrow placement="top">
                            <Typography
                              fontWeight="700"
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {publisher.name}
                            </Typography>
                          </Tooltip>
                          <Typography>{publisher.agentsCount} agents published</Typography>
                        </Paper>
                      ))
                    ) : (
                      <Typography>No publisher data available</Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Paper>
    );
  };

  return (
    <Box>
      <Carousel
        autoPlay={true}
        stopAutoPlayOnHover={true}
        indicators={false}
        index={carouselActiveIndex}
        onChange={(now) => setCarouselActiveIndex(now || 0)}
      >
        {carouselItems.map((item, index) => (
          <CarouselItemComponent
            key={index}
            item={item}
            handleFindAgent={handleFindAgent}
            handleRedirect={handleRedirect}
          />
        ))}
      </Carousel>

      {/* Accessibility indicator dots for the carousel */}
      <Box ref={carouselIndicatorRef} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {carouselItems.map((item, index) => (
          <button
            key={index}
            role="tab"
            aria-label={`Carousel slide ${index + 1} of ${carouselItems.length}: ${item.name}`}
            aria-selected={index === carouselActiveIndex ? 'true' : 'false'}
            tabIndex={0}
            onClick={() => setCarouselActiveIndex(index)}
            style={{
              width: 5,
              height: 5,
              margin: '0 3px',
              borderRadius: '50%',
              padding: '5px',
              backgroundColor: index === carouselActiveIndex ? '#000' : 'grey',
              cursor: 'pointer',
              border: 'none',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default AgentInnovators;
